import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { SERVICE_TYPES, isScheduledType } from "@/lib/constants";
import { ArrowLeft, MapPin, Plus, CalendarDays, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useGeolocation } from "@/hooks/useGeolocation";

export default function CreateRequest() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("");
  const [serviceType, setServiceType] = useState("breakdown");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState(500);
  const [scheduledDate, setScheduledDate] = useState("");
  const [coords, setCoords] = useState(null);
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const geo = useGeolocation({ auto: true });

  const scheduled = isScheduledType(serviceType);
  // tomorrow 09:00 as default minimum
  const minDateValue = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return d.toISOString().slice(0, 16);
  })();

  useEffect(() => {
    api.get("/vehicles").then(({ data }) => {
      setVehicles(data);
      if (data[0]) setVehicleId(data[0].id);
    });
  }, []);

  // Mirror geolocation hook → form coords
  useEffect(() => {
    if (geo.state === "granted" && geo.coords) {
      setCoords({ lat: geo.coords.lat, lng: geo.coords.lng });
      setAddress(`${geo.coords.lat.toFixed(4)}, ${geo.coords.lng.toFixed(4)}`);
    } else if (geo.state === "denied" || geo.state === "error" || geo.state === "unavailable") {
      // Soft-fallback so users with denied GPS can still submit
      if (!coords) {
        setCoords({ lat: -26.2041, lng: 28.0473 });
        setAddress("Johannesburg (default)");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geo.state, geo.coords?.lat, geo.coords?.lng]);

  const submit = async (e) => {
    e.preventDefault();
    if (!vehicleId) {
      toast.error("Add a vehicle first");
      return;
    }
    if (!coords) {
      toast.error("Location required");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/requests", {
        vehicleId, serviceType, description,
        price: Number(price),
        location: { ...coords, address },
      });
      toast.success("Request created");
      navigate(`/app/request/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  return (
    <div>
      <div className="px-6 pt-6 pb-2 flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="create-back-btn"><ArrowLeft /></button>
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">New Request</span>
      </div>

      <div className="px-6 pb-4">
        <h1 className="font-display text-3xl font-black tracking-tight">Tell us what's wrong.</h1>
      </div>

      <form onSubmit={submit} className="px-6 space-y-5 pb-8">
        {/* Vehicle */}
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">Vehicle</label>
          {vehicles.length === 0 ? (
            <Link to="/app/vehicles" className="flex items-center gap-2 mt-1 h-12 border border-dashed border-white/30 rounded-md px-4 text-sm text-[#9CA3AF]" data-testid="add-vehicle-link">
              <Plus size={16} /> Add a vehicle first
            </Link>
          ) : (
            <select
              value={vehicleId}
              onChange={(e) => setVehicleId(e.target.value)}
              data-testid="vehicle-select"
              className="w-full h-12 mt-1 border border-white/20 rounded-md px-4 bg-[#111827] focus:outline-none focus:border-[#3CFF7E]"
            >
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>{v.make} {v.model} {v.year ? `(${v.year})` : ""} {v.plate ? `· ${v.plate}` : ""}</option>
              ))}
            </select>
          )}
        </div>

        {/* Service type grid */}
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">Service Type</label>
          <div className="grid grid-cols-3 gap-2 mt-1">
            {SERVICE_TYPES.map((s) => (
              <button
                type="button"
                key={s.value}
                onClick={() => setServiceType(s.value)}
                data-testid={`service-${s.value}`}
                className={`border rounded-md p-3 text-left transition-all ${
                  serviceType === s.value
                    ? "border-[#3CFF7E] bg-[#3CFF7E] text-[#0A0A0A]"
                    : "border-white/10 bg-[#111827] hover:border-white"
                }`}
              >
                <div className="text-lg">{s.icon}</div>
                <div className="text-xs font-display font-bold mt-1">{s.label}</div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            data-testid="description-input"
            className="w-full mt-1 border border-white/20 rounded-md px-4 py-3 focus:outline-none focus:border-[#3CFF7E]"
            placeholder="E.g. Engine won't start, battery light on..."
          />
        </div>

        {scheduled && (
          <div data-testid="schedule-panel">
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#E6FF3C] font-display flex items-center gap-1">
              <CalendarDays size={12} /> Select Date & Time
            </label>
            <input
              type="datetime-local"
              required
              min={minDateValue}
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              data-testid="scheduled-date-input"
              className="w-full h-12 mt-1 border border-[#E6FF3C]/40 bg-[#E6FF3C]/5 rounded-md px-4 text-white focus:outline-none focus:border-[#E6FF3C]"
            />
            <div className="text-[10px] text-[#9CA3AF] mt-1">
              Must be in the future. You can only cancel up to 24h before the selected time.
            </div>
          </div>
        )}

        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">Budget (ZAR)</label>
          <input
            type="number"
            min="0"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            data-testid="price-input"
            className="w-full h-12 mt-1 border border-white/20 rounded-md px-4 focus:outline-none focus:border-[#3CFF7E]"
          />
        </div>

        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">Location</label>
          <div className="mt-1 flex items-center gap-2">
            <div className="flex-1 h-12 border border-white/20 rounded-md px-4 flex items-center gap-2 text-sm">
              {geo.state === "requesting" ? (
                <Loader2 size={14} className="text-[#3CFF7E] animate-spin" data-testid="location-spinner" />
              ) : (
                <MapPin size={16} className="text-[#3CFF7E]" />
              )}
              <span className="truncate" data-testid="location-text">
                {geo.state === "requesting"
                  ? "Finding your location…"
                  : address || "No location"}
              </span>
            </div>
            <button
              type="button"
              onClick={geo.request}
              data-testid="refresh-location-btn"
              className="h-12 px-4 border border-white/20 rounded-md text-xs font-bold uppercase tracking-wider font-display flex items-center gap-1"
            >
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
          {(geo.state === "denied" || geo.state === "error") && (
            <div className="text-[11px] text-[#E6FF3C] mt-2" data-testid="location-warning">
              {geo.state === "denied"
                ? "Please allow location access so nearby clients can find you. Using a default location for now."
                : (geo.error || "Couldn't get your location.")}
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={loading || vehicles.length === 0}
          data-testid="submit-request-btn"
          className="w-full h-14 bg-[#3CFF7E] text-[#0A0A0A] font-display font-bold uppercase tracking-wider rounded-md active:scale-[0.98] disabled:opacity-60"
        >
          {loading ? "Sending..." : "Send Request"}
        </button>
      </form>
    </div>
  );
}
